/*
 * The mobile app's "Weak Password Detected" prompt (Dashboard.js): right after
 * a student or parent signs in with a password that misses the strength rules,
 * the dashboard offers to change it — unless the school stops that user type
 * from changing passwords.
 *
 * The password itself is never kept: the login screen records only whether it
 * was weak (`rememberLoginPasswordStrength`), in sessionStorage so the prompt
 * shows once per sign-in in this tab.
 */

/** sessionStorage key: '1' while a weak-password prompt is owed. */
export const WEAK_PASSWORD_FLAG_KEY = 'sp_weak_login_password'

/** Special characters the mobile rule accepts. */
const SPECIAL_CHARACTER = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/

/**
 * Mobile `isWeakPassword`: weak unless it has 8+ characters with an upper-
 * and a lower-case letter, a digit and a special character.
 * @param password the password typed at login
 */
export const isWeakPassword = (password: string | null | undefined) => {
  if (!password) return true
  return !(
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /[0-9]/.test(password) &&
    SPECIAL_CHARACTER.test(password) &&
    password.length >= 8
  )
}

const storage = (): Storage | null => {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    return null
  }
}

/**
 * Called by the login screen after a successful sign-in: remembers whether
 * the password was weak (never the password).
 * @param password the password the user signed in with
 */
export const rememberLoginPasswordStrength = (password: string | null | undefined) => {
  const store = storage()
  if (!store) return
  try {
    if (isWeakPassword(password)) store.setItem(WEAK_PASSWORD_FLAG_KEY, '1')
    else store.removeItem(WEAK_PASSWORD_FLAG_KEY)
  } catch {
    // storage full / blocked: the prompt is only a nudge
  }
}

/** Whether a weak-password prompt is owed for this sign-in. */
export const isWeakPasswordPromptPending = () => {
  try {
    return storage()?.getItem(WEAK_PASSWORD_FLAG_KEY) === '1'
  } catch {
    return false
  }
}

/** The prompt was shown (or is not allowed): never show it again this sign-in. */
export const clearWeakPasswordPrompt = () => {
  try {
    storage()?.removeItem(WEAK_PASSWORD_FLAG_KEY)
  } catch {
    // ignore
  }
}
